/**
 * features/finance/PeriodEnd.tsx
 * ------------------------------------------------------------------
 * The month, and the year.
 *
 * Three jobs that all answer the same question — what belongs to this period
 * that nobody has posted yet?
 *
 *   STANDING ENTRIES are the same journal every month: the rent, the security
 *   contract, the depreciation charge. Someone works them out once.
 *
 *   PREPAYMENTS AND ACCRUALS are one amount spread across months. A year of
 *   insurance paid in January is not a January cost — recording it as one
 *   makes January look terrible and the other eleven better than they were,
 *   and the accounts stop describing how the school actually runs.
 *
 *   YEAR END empties every income and expense account into the accumulated
 *   fund. Without it next year's income statement silently includes this
 *   year's.
 *
 * READ FIRST, POST SECOND. Every one of these shows exactly what it is about
 * to write before it writes anything. A bursar looking at forty journals has
 * a chance to notice that one of them is wrong; a bursar looking at "done"
 * does not — and these entries post unattended, so nobody reads them
 * afterwards either.
 *
 * THE RUN IS SAFE TO REPEAT. Each journal's id derives from its template and
 * its month, so pressing the button twice posts once. Somebody unsure
 * whether last month's run finished will press it again, and that has to be
 * the right thing to do.
 * ------------------------------------------------------------------ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  CalendarCheck, Repeat, Hourglass, Archive, Play, Plus, Trash2, AlertTriangle,
  CheckCircle2, Landmark, Scale,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { FINANCE_COLLECTIONS } from '@/lib/financeCore';
import { useGlData } from '@/features/finance/useGlData';
import {
  deferralEndPeriod, deferralOutstanding, deferralInstalments,
  recurringProblem, deferralProblem,
  type RecurringTemplate, type Deferral, type DeferralKind,
} from '@/lib/recurring';
import { buildYearEndPosition, yearEndProblem } from '@/lib/yearEnd';
import {
  saveRecurringTemplate, stopRecurringTemplate, createDeferral,
  planPeriodEnd, runPeriodEnd, closeYear, resolveKeys, postedSourceIds,
  type SweepPlan, type SweepResult,
} from '@/features/finance/periodEndWrite';
import type { JournalLine } from '@/lib/gl';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, btnPrimary, btnGhost,
} from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const thisMonth = () => new Date().toISOString().slice(0, 7);
const todayIso = () => new Date().toISOString().slice(0, 10);

type Tab = 'run' | 'standing' | 'deferrals' | 'year';

const TABS: { id: Tab; label: string; icon: typeof Repeat }[] = [
  { id: 'run', label: 'Run the month', icon: Play },
  { id: 'standing', label: 'Standing entries', icon: Repeat },
  { id: 'deferrals', label: 'Prepayments & accruals', icon: Hourglass },
  { id: 'year', label: 'Year end', icon: Archive },
];

/** Live subscription, mirroring useFinanceData's shape for two small sets. */
function useRows<T>(name: string): { rows: T[]; ready: boolean } {
  const [rows, setRows] = useState<T[]>([]);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, name)),
      (snap) => { setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T)); setReady(true); },
      () => setReady(true),
    );
    return unsub;
  }, [name]);
  return { rows, ready };
}

export default function PeriodEnd() {
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canPost = perms.canPostJournals;
  const actor = useMemo(() => ({
    uid: userData?.uid, name: userData?.displayName, displayName: userData?.displayName, email: userData?.email,
  }), [userData]);

  const [tab, setTab] = useState<Tab>('run');
  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const templates = useRows<RecurringTemplate>(FINANCE_COLLECTIONS.recurringTemplates);
  const deferrals = useRows<Deferral>(FINANCE_COLLECTIONS.deferrals);

  const postable = useMemo(
    () => gl.accounts.filter((a) => a.isPostable && a.active).sort((a, b) => a.code.localeCompare(b.code)),
    [gl.accounts],
  );

  const keys = useMemo(
    () => resolveKeys(gl.accounts, ['PREPAYMENTS', 'ACCRUALS', 'SURPLUS_CURRENT', 'ACCUM_FUND']),
    [gl.accounts],
  );
  const deferralAccounts = {
    prepayments: keys.found.PREPAYMENTS ?? '',
    accruals: keys.found.ACCRUALS ?? '',
  };

  if (gl.loading || !templates.ready || !deferrals.ready) {
    return <div className="p-8 flex justify-center"><Spinner /></div>;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={CalendarCheck}
        title="Period End"
        subtitle="Standing entries, costs that belong to another month, and closing the year"
      />

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={tab === t.id ? btnPrimary : btnGhost}
            >
              <Icon size={15} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'run' && (
        <RunPanel
          templates={templates.rows}
          deferrals={deferrals.rows}
          deferralAccounts={deferralAccounts}
          missing={keys.missing.filter((k) => k === 'PREPAYMENTS' || k === 'ACCRUALS')}
          canPost={canPost}
          actor={actor}
        />
      )}

      {tab === 'standing' && (
        <StandingPanel
          templates={templates.rows}
          accounts={postable.map((a) => ({ code: a.code, name: a.name }))}
          canPost={canPost}
          actor={actor}
        />
      )}

      {tab === 'deferrals' && (
        <DeferralsPanel
          deferrals={deferrals.rows}
          accounts={postable.map((a) => ({ code: a.code, name: a.name }))}
          deferralAccounts={deferralAccounts}
          missing={keys.missing.filter((k) => k === 'PREPAYMENTS' || k === 'ACCRUALS')}
          canPost={canPost}
          actor={actor}
        />
      )}

      {tab === 'year' && (
        <YearEndPanel
          fy={fy}
          setFy={setFy}
          gl={gl}
          missing={keys.missing.filter((k) => k === 'SURPLUS_CURRENT' || k === 'ACCUM_FUND')}
          surplusCode={keys.found.SURPLUS_CURRENT ?? ''}
          fundCode={keys.found.ACCUM_FUND ?? ''}
          canPost={canPost}
          actor={actor}
        />
      )}
    </div>
  );
}

/* ============================ run the month ============================ */

function RunPanel({
  templates, deferrals, deferralAccounts, missing, canPost, actor,
}: {
  templates: RecurringTemplate[];
  deferrals: Deferral[];
  deferralAccounts: { prepayments: string; accruals: string };
  missing: string[];
  canPost: boolean;
  actor: Parameters<typeof runPeriodEnd>[1];
}) {
  const [upTo, setUpTo] = useState(thisMonth());
  const [plan, setPlan] = useState<SweepPlan | null>(null);
  const [result, setResult] = useState<SweepResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (missing.length) return;
    setBusy(true);
    setErr(null);
    try {
      setPlan(await planPeriodEnd(templates, deferrals, upTo, deferralAccounts));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [templates, deferrals, upTo, deferralAccounts, missing.length]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function run() {
    if (!plan) return;
    setBusy(true);
    setErr(null);
    try {
      setResult(await runPeriodEnd(plan, actor));
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader icon={Play} title="What this month owes" subtitle="Nothing is written until you say so" />
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Up to and including
              </label>
              <input
                type="month"
                value={upTo}
                onChange={(e) => { setUpTo(e.target.value); setResult(null); }}
                className={glassFieldClass('py-2')}
              />
            </div>
            <button type="button" className={btnGhost} disabled={busy} onClick={() => { void refresh(); }}>
              Recheck
            </button>
          </div>

          {missing.length > 0 && (
            <p className="text-sm text-red-700 dark:text-red-300">
              No account carries {missing.join(' or ')}. Prepayments and accruals cannot be
              released until the chart of accounts has them.
            </p>
          )}

          {err && <p className="text-sm text-red-700 dark:text-red-300">{err}</p>}

          {plan && plan.totalEntries === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Nothing outstanding to {upTo}. Everything the calendar says is owed has already
              been posted.
            </p>
          )}

          {plan && plan.totalEntries > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="py-2 pr-4">Month</th>
                      <th className="py-2 pr-4">Entry</th>
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...plan.recurring.flatMap((r) => r.entries), ...plan.deferrals.flatMap((r) => r.entries)]
                      .sort((a, b) => a.period.localeCompare(b.period))
                      .map((e) => (
                        <tr key={e.sourceId} className="border-b border-gray-50 dark:border-gray-800">
                          <td className="py-2 pr-4 font-mono text-xs">{e.period}</td>
                          <td className="py-2 pr-4">{e.narration}</td>
                          <td className="py-2 pr-4 text-gray-500 dark:text-gray-400">{e.date}</td>
                          <td className="py-2 text-right tabular-nums">
                            {money(e.lines.reduce((a, l) => a + l.dr, 0))}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <button type="button" className={btnPrimary} disabled={!canPost || busy} onClick={() => { void run(); }}>
                  {busy ? 'Posting…' : `Post ${plan.totalEntries} journal${plan.totalEntries === 1 ? '' : 's'}`}
                </button>
              </div>
              {!canPost && (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-right">
                  You can see what is owed but not post it.
                </p>
              )}
            </>
          )}

          {result && (
            <div className="rounded-lg px-4 py-3 text-sm bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
              {result.posted} posted
              {result.skipped > 0 && `, ${result.skipped} were already there`}
              {result.failures.length > 0 && `, ${result.failures.length} failed`}.
            </div>
          )}

          {result && result.failures.length > 0 && (
            <div className="space-y-1">
              {/* Listed rather than summarised: the rest of the month posted,
                  and these are the ones somebody has to go and fix. */}
              {result.failures.map((f) => (
                <p key={f.sourceId} className="text-sm text-red-700 dark:text-red-300">
                  <span className="font-medium">{f.narration}</span> — {f.error}
                </p>
              ))}
            </div>
          )}
        </div>
      </SoftCard>
    </div>
  );
}

/* =========================== standing entries =========================== */

interface DraftLine { accountCode: string; dr: string; cr: string }
const emptyLine = (): DraftLine => ({ accountCode: '', dr: '', cr: '' });

function StandingPanel({
  templates, accounts, canPost, actor,
}: {
  templates: RecurringTemplate[];
  accounts: { code: string; name: string }[];
  canPost: boolean;
  actor: Parameters<typeof saveRecurringTemplate>[1];
}) {
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState('');
  const [startPeriod, setStartPeriod] = useState(thisMonth());
  const [endPeriod, setEndPeriod] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const draft = useMemo<JournalLine[]>(
    () => lines
      .filter((l) => l.accountCode && (Number(l.dr) || Number(l.cr)))
      .map((l) => ({ accountCode: l.accountCode, dr: Number(l.dr) || 0, cr: Number(l.cr) || 0 })),
    [lines],
  );

  const dr = draft.reduce((a, l) => a + l.dr, 0);
  const cr = draft.reduce((a, l) => a + l.cr, 0);
  const problem = recurringProblem({ name, lines: draft, startPeriod, endPeriod: endPeriod || undefined });

  function reset() {
    setComposing(false);
    setName(''); setStartPeriod(thisMonth()); setEndPeriod('');
    setLines([emptyLine(), emptyLine()]);
  }

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await saveRecurringTemplate(
        { name, lines: draft, startPeriod, endPeriod: endPeriod || undefined, active: true },
        actor,
      );
      setMsg({ ok: true, text: `"${name}" will post at the end of every month from ${startPeriod}.` });
      reset();
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function stop(t: RecurringTemplate) {
    const reason = window.prompt(`Stop "${t.name}"? Say why — the journals it posted refer to it.`);
    if (!reason) return;
    try {
      await stopRecurringTemplate(t.id, reason, actor);
      setMsg({ ok: true, text: `"${t.name}" stopped.` });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    }
  }

  return (
    <div className="space-y-4">
      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Repeat}
          title="Standing entries"
          subtitle="The same journal every month, posted by the run"
          right={canPost && !composing
            ? <button type="button" className={btnPrimary} onClick={() => setComposing(true)}>
              <Plus size={15} /> New
            </button>
            : undefined}
        />
        <div className="p-5 space-y-4">
          {msg && (
            <div className={`rounded-lg px-4 py-3 text-sm ${msg.ok
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
            >
              {msg.text}
            </div>
          )}

          {composing && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Security contract"
                    className={glassFieldClass('py-2')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">From</label>
                  <input type="month" value={startPeriod} onChange={(e) => setStartPeriod(e.target.value)} className={glassFieldClass('py-2')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                    Until <span className="text-gray-400">(blank = until stopped)</span>
                  </label>
                  <input type="month" value={endPeriod} onChange={(e) => setEndPeriod(e.target.value)} className={glassFieldClass('py-2')} />
                </div>
              </div>

              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <select
                      value={l.accountCode}
                      onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, accountCode: e.target.value } : x)))}
                      className={glassFieldClass('py-2 col-span-6')}
                    >
                      <option value="">Account…</option>
                      {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                    </select>
                    <input
                      value={l.dr}
                      onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, dr: e.target.value, cr: '' } : x)))}
                      placeholder="Debit"
                      inputMode="decimal"
                      className={glassFieldClass('py-2 col-span-3 text-right')}
                    />
                    <input
                      value={l.cr}
                      onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, cr: e.target.value, dr: '' } : x)))}
                      placeholder="Credit"
                      inputMode="decimal"
                      className={glassFieldClass('py-2 col-span-3 text-right')}
                    />
                  </div>
                ))}
                <button type="button" className={btnGhost} onClick={() => setLines([...lines, emptyLine()])}>
                  <Plus size={14} /> Add a line
                </button>
              </div>

              {/* THE RUNNING DIFFERENCE, always visible. An unbalanced
                  template would fail silently every month. */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Debits {money(dr)} · Credits {money(cr)}
                </span>
                <span className={dr === cr && dr > 0
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-amber-600 dark:text-amber-400'}
                >
                  {dr === cr && dr > 0
                    ? 'Balances'
                    : `Out of balance by ${money(Math.abs(dr - cr))} — ${dr > cr ? 'credits' : 'debits'} short`}
                </span>
              </div>

              {problem && <p className="text-sm text-amber-700 dark:text-amber-300">{problem}</p>}

              <div className="flex justify-end gap-2">
                <button type="button" className={btnGhost} onClick={reset}>Cancel</button>
                <button type="button" className={btnPrimary} disabled={!!problem || busy} onClick={() => { void save(); }}>
                  {busy ? 'Saving…' : 'Save template'}
                </button>
              </div>
            </div>
          )}

          {templates.length === 0 && !composing && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No standing entries. Anything the school posts identically every month belongs here.
            </p>
          )}

          {templates.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">From</th>
                    <th className="py-2 pr-4">Until</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4">{t.name}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{t.startPeriod}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{t.endPeriod ?? '—'}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {money(t.lines.reduce((a, l) => a + (l.dr || 0), 0))}
                      </td>
                      <td className="py-2 pr-4">
                        {t.active
                          ? <span className="text-emerald-600 dark:text-emerald-400">Running</span>
                          : <span className="text-gray-400" title={t.stoppedReason}>Stopped</span>}
                      </td>
                      <td className="py-2 text-right">
                        {canPost && t.active && (
                          <button type="button" className={btnGhost} onClick={() => { void stop(t); }}>
                            <Trash2 size={14} /> Stop
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SoftCard>
    </div>
  );
}

/* ========================= prepayments & accruals ======================= */

function DeferralsPanel({
  deferrals, accounts, deferralAccounts, missing, canPost, actor,
}: {
  deferrals: Deferral[];
  accounts: { code: string; name: string }[];
  deferralAccounts: { prepayments: string; accruals: string };
  missing: string[];
  canPost: boolean;
  actor: Parameters<typeof createDeferral>[2];
}) {
  const [composing, setComposing] = useState(false);
  const [kind, setKind] = useState<DeferralKind>('prepayment');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [accountCode, setAccountCode] = useState('');
  const [startPeriod, setStartPeriod] = useState(thisMonth());
  const [months, setMonths] = useState('12');
  const [recognisedOn, setRecognisedOn] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const input = {
    kind, description, amount: Number(amount) || 0, accountCode, startPeriod,
    months: Number(months) || 0, recognisedOn,
  };
  const problem = deferralProblem(input);
  const perMonth = Number(amount) > 0 && Number(months) > 0
    ? deferralInstalments(Number(amount), Number(months))
    : [];

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      await createDeferral(input, deferralAccounts, actor);
      setMsg({
        ok: true,
        text: `${description} recorded. ${money(perMonth[0] ?? 0)} a month `
          + `from ${startPeriod} to ${deferralEndPeriod({ startPeriod, months: Number(months) })}.`,
      });
      setComposing(false);
      setDescription(''); setAmount(''); setAccountCode('');
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  /* Measured against what has ACTUALLY posted, not against an empty set.
     "Yet to be charged" answered from the deferral records alone would
     always report the full amount and would never once be right after the
     first release — a figure that reads true and is not. */
  const [posted, setPosted] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => { void postedSourceIds().then(setPosted).catch(() => setPosted(new Set())); }, [deferrals]);

  const totalHeld = deferrals
    .filter((d) => d.active)
    .reduce((a, d) => a + deferralOutstanding(d, posted), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatChip
          icon={Hourglass}
          label={`Open — ${deferrals.filter((d) => d.active).length}`}
          value={String(deferrals.filter((d) => d.active).length)}
        />
        <StatChip icon={Scale} label="Yet to be charged to a month" value={money(totalHeld)} />
        <StatChip
          icon={CheckCircle2}
          label="Fully released"
          value={String(deferrals.filter((d) => !d.active).length)}
        />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Hourglass}
          title="Prepayments & accruals"
          subtitle="A cost that belongs to months other than the one it was paid in"
          right={canPost && !composing && missing.length === 0
            ? <button type="button" className={btnPrimary} onClick={() => setComposing(true)}>
              <Plus size={15} /> New
            </button>
            : undefined}
        />
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
            A <strong>prepayment</strong> is something bought and not yet consumed — a year of
            insurance paid in January. The cost moves out of the expense into an asset and a
            twelfth returns each month. An <strong>accrual</strong> is the mirror: something
            consumed and not yet billed, held as a liability until the invoice arrives.
          </p>

          {missing.length > 0 && (
            <p className="text-sm text-red-700 dark:text-red-300">
              No account carries {missing.join(' or ')}. Add it to the chart of accounts first —
              a deferral with nowhere to sit is a cost that simply disappears.
            </p>
          )}

          {msg && (
            <div className={`rounded-lg px-4 py-3 text-sm ${msg.ok
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
            >
              {msg.text}
            </div>
          )}

          {composing && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Type</label>
                  <select value={kind} onChange={(e) => setKind(e.target.value as DeferralKind)} className={glassFieldClass('py-2')}>
                    <option value="prepayment">Prepayment — paid now, consumed later</option>
                    <option value="accrual">Accrual — consumed now, billed later</option>
                  </select>
                </div>
                <div className="lg:col-span-2">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">What it is</label>
                  <input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Insurance — 2026 policy year"
                    className={glassFieldClass('py-2')}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Amount</label>
                  <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" className={glassFieldClass('py-2 text-right')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Charged to</label>
                  <select value={accountCode} onChange={(e) => setAccountCode(e.target.value)} className={glassFieldClass('py-2')}>
                    <option value="">Account…</option>
                    {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Recognised on</label>
                  <input type="date" value={recognisedOn} onChange={(e) => setRecognisedOn(e.target.value)} className={glassFieldClass('py-2')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">First month</label>
                  <input type="month" value={startPeriod} onChange={(e) => setStartPeriod(e.target.value)} className={glassFieldClass('py-2')} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Over how many months</label>
                  <input value={months} onChange={(e) => setMonths(e.target.value)} inputMode="numeric" className={glassFieldClass('py-2 text-right')} />
                </div>
              </div>

              {perMonth.length > 0 && (
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  {money(perMonth[0])} a month
                  {perMonth[perMonth.length - 1] !== perMonth[0]
                    && `, and ${money(perMonth[perMonth.length - 1])} in the last month`}
                  , from {startPeriod} to {deferralEndPeriod({ startPeriod, months: Number(months) })}.
                </p>
              )}

              {problem && <p className="text-sm text-amber-700 dark:text-amber-300">{problem}</p>}

              <div className="flex justify-end gap-2">
                <button type="button" className={btnGhost} onClick={() => setComposing(false)}>Cancel</button>
                <button type="button" className={btnPrimary} disabled={!!problem || busy} onClick={() => { void save(); }}>
                  {busy ? 'Recording…' : 'Record it'}
                </button>
              </div>
            </div>
          )}

          {deferrals.length === 0 && !composing && (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nothing recorded.</p>
          )}

          {deferrals.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-4">What</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 pr-4 text-right">Amount</th>
                    <th className="py-2 pr-4">Spread</th>
                    <th className="py-2 pr-4 text-right">Still to charge</th>
                    <th className="py-2 pr-4">Account</th>
                  </tr>
                </thead>
                <tbody>
                  {deferrals.map((d) => (
                    <tr key={d.id} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4">{d.description}</td>
                      <td className="py-2 pr-4 capitalize">{d.kind}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(d.amount)}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {d.startPeriod} → {deferralEndPeriod(d)}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {money(deferralOutstanding(d, posted))}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{d.accountCode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </SoftCard>
    </div>
  );
}

/* =============================== year end =============================== */

function YearEndPanel({
  fy, setFy, gl, missing, surplusCode, fundCode, canPost, actor,
}: {
  fy: string;
  setFy: (v: string) => void;
  gl: ReturnType<typeof useGlData>;
  missing: string[];
  surplusCode: string;
  fundCode: string;
  canPost: boolean;
  actor: Parameters<typeof closeYear>[4];
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const position = useMemo(
    () => buildYearEndPosition(gl.journals, gl.accounts, fy),
    [gl.journals, gl.accounts, fy],
  );
  const problem = yearEndProblem(position);
  const blocked = !canPost || !!problem || missing.length > 0;

  async function close() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await closeYear(
        gl.journals, gl.accounts, fy,
        { surplus: surplusCode, accumulatedFund: fundCode },
        actor,
      );
      setMsg({
        ok: true,
        text: `${fy} closed as ${r.closingJournal}`
          + (r.transferJournal ? ` and ${r.transferJournal}` : '')
          + `. ${r.surplus >= 0 ? 'Surplus' : 'Deficit'} of ${money(Math.abs(r.surplus))} `
          + `to the accumulated fund; ${r.periodsLocked} month${r.periodsLocked === 1 ? '' : 's'} locked.`,
      });
      setConfirming(false);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const years = Array.from({ length: 5 }, (_, i) => `FY${new Date().getFullYear() - i}`);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatChip icon={Landmark} label="Income for the year" value={money(position.totalIncome)} />
        <StatChip icon={Scale} label="Expenditure" value={money(position.totalExpense)} />
        <StatChip
          icon={position.surplus >= 0 ? CheckCircle2 : AlertTriangle}
          label={position.surplus >= 0 ? 'Surplus' : 'Deficit'}
          value={money(Math.abs(position.surplus))}
        />
      </div>

      <SoftCard pad="p-0" hover={false}>
        <PanelHeader
          icon={Archive}
          title={`Closing ${fy}`}
          subtitle="Every income and expense account back to nil, and the result to the fund"
          right={(
            <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('py-1.5')}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          )}
        />
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 max-w-3xl">
            Income and expense accounts describe <em>one year</em>; assets, liabilities and the
            fund describe <em>a moment</em> and carry straight over. Closing empties the first
            group into the accumulated fund so that next year&rsquo;s income statement is next
            year&rsquo;s. Every month in the year is locked at the same time — a closed year
            that still accepts postings is not closed.
          </p>

          {missing.length > 0 && (
            <p className="text-sm text-red-700 dark:text-red-300">
              No account carries {missing.join(' or ')}. The year cannot be closed without them.
            </p>
          )}

          {position.reversedCount > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {position.reversedCount} journal{position.reversedCount === 1 ? ' was' : 's were'} reversed
              during the year. Both the original and its reversal are counted — they cancel, and
              excluding either would throw the year out of balance.
            </p>
          )}

          {position.rows.length > 0 && (
            <div className="overflow-x-auto max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-4">Account</th>
                    <th className="py-2 pr-4">Type</th>
                    <th className="py-2 text-right">Closes with</th>
                  </tr>
                </thead>
                <tbody>
                  {position.rows.map((r) => (
                    <tr key={r.accountCode} className="border-b border-gray-50 dark:border-gray-800">
                      <td className="py-2 pr-4">
                        <span className="font-mono text-xs">{r.accountCode}</span> {r.name}
                      </td>
                      <td className="py-2 pr-4 capitalize text-gray-500 dark:text-gray-400">{r.type}</td>
                      <td className="py-2 text-right tabular-nums">
                        {r.net < 0 ? `Dr ${money(-r.net)}` : `Cr ${money(r.net)}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {problem && <p className="text-sm text-red-700 dark:text-red-300">{problem}</p>}

          {msg && (
            <div className={`rounded-lg px-4 py-3 text-sm ${msg.ok
              ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
              : 'bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200'}`}
            >
              {msg.text}
            </div>
          )}

          <div className="flex justify-end gap-2">
            {confirming ? (
              <>
                <button type="button" className={btnGhost} onClick={() => setConfirming(false)}>Cancel</button>
                <button type="button" className={btnPrimary} disabled={busy} onClick={() => { void close(); }}>
                  {busy ? 'Closing…' : `Yes — close ${fy} and lock its months`}
                </button>
              </>
            ) : (
              <button type="button" className={btnPrimary} disabled={blocked} onClick={() => { setMsg(null); setConfirming(true); }}>
                Close {fy}
              </button>
            )}
          </div>

          {confirming && (
            <p className="text-sm text-amber-700 dark:text-amber-300 text-right">
              This posts two journals and locks every month in {fy}. Reopening a month afterwards
              is possible, but the closing entry will already have been taken.
            </p>
          )}
        </div>
      </SoftCard>
    </div>
  );
}
